/**
 * Represents the values stored on the OrderState Store
 * @interface OrderState
 */

import { ConfirmedOrder } from 'app/model/confirmed-order';
import { OrderFile } from 'app/model/order-file';
import { OrderItem } from 'app/model/order-item';
import { PurchaseOrder } from 'app/model/purchase-order';
import { QuoteItem } from 'app/model/quote';
import { ShoppingCart } from 'app/model/shopping-cart';

/**
 * Represents the state structure for order management in the NgRx store.
 *
 * @interface OrderState
 *
 * @property {OrderItem[]} orderItemsListSelected - Array of order items that have been selected by the user
 *
 * @property {ShoppingCart[]} quotes - Collection of shopping cart quotes
 *
 * @property {PurchaseOrder[]} purchaseOrders - Array of purchase orders created from quotes
 *
 * @property {ConfirmedOrder[]} confirmedOrders - Collection of orders that have been confirmed
 *
 * @property {Array<{purchaseOrderId: string; quoteId: string; items: OrderItem[];}>} purchaseOrderItems
 * Array of purchase order items grouped by purchase order ID and quote ID.
 * Each entry contains:
 * - `purchaseOrderId`: Unique identifier for the purchase order
 * - `quoteId`: Associated quote identifier
 * - `items`: Array of order items belonging to this purchase order
 *
 * @property {Array<{confirmedOrderId: string; quoteId?: string; items: OrderItem[];}>} confirmedOrderItems
 * Array of confirmed order items grouped by confirmed order ID.
 * Each entry contains:
 * - `confirmedOrderId`: Unique identifier for the confirmed order
 * - `quoteId`: Optional associated quote identifier
 * - `items`: Array of order items belonging to this confirmed order
 *
 * @property {OrderFile | null} uploadedOrderFile - File that has been uploaded for order processing, or null if no file is uploaded
 *
 * @property {QuoteItem[] | null} orderList - List of quote items for display or processing, or null if not loaded
 *
 * @property {number} totalLoadOrders - Total count of orders loaded or available
 *
 * @property {boolean} isLoadingOrders - Flag indicating whether orders are currently being loaded
 *
 * @example
 * const initialState: OrderState = {
 *   orderItemsListSelected: [],
 *   quotes: [],
 *   purchaseOrders: [],
 *   confirmedOrders: [],
 *   purchaseOrderItems: [],
 *   confirmedOrderItems: [],
 *   uploadedOrderFile: null,
 *   orderList: null,
 *   totalLoadOrders: 0,
 *   isLoadingOrders: false
 * };
 */
export interface OrderState {
  confirmedOrderItems: Array<{
    confirmedOrderId: string;
    quoteId?: string;
    items: OrderItem[];
  }>;
  confirmedOrders: ConfirmedOrder[];
  isLoadingOrders: boolean;
  orderItemsListSelected: OrderItem[];
  orderList: QuoteItem[] | null;
  purchaseOrderItems: Array<{
    purchaseOrderId: string;
    quoteId: string;
    items: OrderItem[];
  }>;
  purchaseOrders: PurchaseOrder[];
  quotes: ShoppingCart[];
  totalLoadOrders: number;

  uploadedOrderFile: OrderFile | null;
}

/**
 * Specify initial state for OrderState
 */
export const initialOrderState: OrderState = {
  confirmedOrderItems: [],
  confirmedOrders: [],
  isLoadingOrders: false,
  orderItemsListSelected: [],
  orderList: null,
  purchaseOrderItems: [],
  purchaseOrders: [],
  quotes: [],
  totalLoadOrders: 0,
  uploadedOrderFile: null,
};
